import { useState } from "react";

import { Bot, Check } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useCan } from "@/lib/auth/useCan";

import { useAIModels, useUpdateSession, type PlanningSession } from "../api";

/** Strip the ":latest" noise; keep meaningful tags like ":30b". */
function shortName(model: string): string {
  return model.replace(/:latest$/, "");
}

/**
 * The session's AI chip: shows which local model reviews, categorizes, and
 * summarizes in this session. Session managers can pick a different model or
 * reset to the planning default; everyone else just sees what's running.
 */
export function AIModelChip({ session }: { session: PlanningSession }) {
  const canWrite = useCan("planning.write");
  const canAdmin = useCan("planning.admin");
  const canManage = canWrite || canAdmin;
  const models = useAIModels();
  const update = useUpdateSession();
  const [open, setOpen] = useState(false);

  const effective = session.ai_model ?? models.data?.default_model ?? null;
  const label = effective ? shortName(effective) : "AI";

  const pick = (value: string) => {
    update.mutate(
      { id: session.id, ai_model: value },
      {
        onSuccess: () => {
          toast.success(value ? `AI model set to ${shortName(value)}` : "AI model reset to default");
          setOpen(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="h-7 gap-1.5 text-xs"
        title={
          session.ai_model
            ? `Session pinned to ${session.ai_model}`
            : `Planning default${effective ? `: ${effective}` : ""}`
        }
        onClick={() => setOpen(true)}
      >
        <Bot className="h-3.5 w-3.5 text-brand" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Session AI model</DialogTitle>
            <DialogDescription>
              The local model that reviews note placement, sorts notes into
              themes, and writes lane summaries for this session.
            </DialogDescription>
          </DialogHeader>

          {!models.data?.reachable ? (
            <p className="text-sm text-muted-foreground">
              The local model host isn&apos;t reachable right now — AI features are
              paused, but nothing else is affected.
            </p>
          ) : (
            <div className="space-y-1.5">
              <ModelRow
                name={`Planning default (${models.data.default_model ? shortName(models.data.default_model) : "auto"})`}
                sizeGb={null}
                active={session.ai_model === null}
                clickable={canManage}
                onPick={() => pick("")}
              />
              {models.data.models.map((m) => (
                <ModelRow
                  key={m.name}
                  name={shortName(m.name)}
                  sizeGb={m.size_gb}
                  active={session.ai_model === m.name}
                  recommended={m.is_default}
                  clickable={canManage}
                  onPick={() => pick(m.name)}
                />
              ))}
              <p className="pt-1 text-xs text-muted-foreground">
                Heads-up: the default is picked for speed — other models can take
                minutes per action and may hit the gateway timeout.
              </p>
              {!canManage ? (
                <p className="text-xs text-muted-foreground">
                  Only session managers can change the model.
                </p>
              ) : null}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function ModelRow({
  name,
  sizeGb,
  active,
  recommended = false,
  clickable,
  onPick,
}: {
  name: string;
  sizeGb: number | null;
  active: boolean;
  recommended?: boolean;
  clickable: boolean;
  onPick: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={onPick}
      className={cn(
        "flex w-full items-center gap-2 rounded-md border px-3 py-2 text-left text-sm transition-colors",
        active ? "border-brand bg-brand/5" : "border-border",
        clickable && !active && "hover:border-brand/50",
        !clickable && "cursor-default",
      )}
    >
      <span className="min-w-0 flex-1 truncate font-medium">
        {name}
        {recommended ? (
          <span className="ml-2 text-[10px] font-semibold uppercase tracking-wider text-brand">
            default
          </span>
        ) : null}
      </span>
      {sizeGb !== null ? (
        <span className="shrink-0 text-xs text-muted-foreground">{sizeGb} GB</span>
      ) : null}
      {active ? <Check className="h-4 w-4 shrink-0 text-brand" /> : null}
    </button>
  );
}
